import React, { useContext } from 'react';
import classnames from 'classnames';
import AppContext from 'app/AppContext';
import { usePointedTime } from 'utils/components/TimeSeriesChart/timepoint';
import { FillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import { FillsChartType } from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { fillBlobSize } from './series/fillPricePoints';
import { deriveLegendData } from './orderFillsChartLegendData';
import { OrderFillsChartMultiLegend } from './OrderFillsChartMultiLegend';
import FillsChartToggleLegend from 'widgets/fillsGraph/legend/FillsChartToggleLegend';
import { avgExecLabel, percentDeltaLabel, priceLabel } from '../legend/labels';
import { indexTooltip } from '../performanceVsIndex/help/performanceVsIndexLegendTooltips';
import {
    priceTooltip,
    avgExecutionPriceTooltip,
    percentCompleteTooltip,
} from './help/orderFillsLegendTooltip';
import OrderFillsHelp from './help/OrderFillsHelp';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import TimeSeriesLegend from 'utils/components/TimeSeriesChart/TimeSeriesLegend';
import { useLegendStatus } from 'widgets/fillsGraph/legendStatus';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import { useHorizontalZoomContext } from 'utils/components/Zoom/ZoomContext';
import { OrderFillsChartPriceCandleLegend } from 'widgets/fillsGraph/orderFills/OrderFillsChartPriceCandleLegend';
import { useXAxisContext } from 'utils/components/TimeSeriesChart/XAxisContext';

interface OrderFillsChartLegendProps {
    avgExecAndVwapDecimals: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    fillsGraphContainerLeft: number;
}

export default function OrderFillsChartLegend(
    props: OrderFillsChartLegendProps
) {
    const {
        avgExecAndVwapDecimals,
        fillsGraphData,
        fillsGraphPortfolioData,
        fillsGraphContainerLeft,
    } = props;

    const { relativePerformanceIndex } = fillsGraphData;

    const time = usePointedTime('OrderFillsChartLegend');
    const { startTime, endTime } = useXAxisContext();
    const hoveringOverRhs =
        time && time > startTime + (endTime - startTime) / 2;
    const { numberFormat, timeFormat } = useContext(AppContext);

    const { widthToInterval } = useHorizontalZoomContext();

    const fillHoverInterval = widthToInterval(fillBlobSize);

    const legendData = deriveLegendData(
        fillsGraphData,
        numberFormat,
        timeFormat,
        time,
        fillHoverInterval,
        avgExecAndVwapDecimals
    );

    const {
        allOrderFillsHidden: allHidden,
        displayAvgExecutionLegend,
        displayPercentageCompletedLegend,
        displayIndexLegend,
        vwapLegendLabel,
        vwapLegendTooltip,
        hideFillPriceLegend,
        hideFillVolumeLegend,
        displayPriceCandleLegends,
    } = useLegendStatus(fillsGraphData, fillsGraphPortfolioData);

    const locked = useSelectLocked();

    const showFillLegend = legendData.showFillLegend || !locked;

    const hoveringOverBucket = time && legendData.bucketTime;

    const showCandleLegends = hoveringOverBucket && displayPriceCandleLegends;
    const visibleFillLegends = !locked
        ? 2
        : showFillLegend
        ? (hideFillPriceLegend ? 0 : 1) + (hideFillVolumeLegend ? 0 : 1)
        : 0;
    // "allHidden" => the always shown legend items are all hidden. The fill legends may still be displayed on mouseover of a fill

    return (
        <div
            className={classnames(
                'OrderFillsChartLegend',
                `${showFillLegend ? 'show-fill-legend' : ''}`,
                `${allHidden ? 'all-static-hidden' : ''}`,
                `${hoveringOverRhs ? 'hovering-over-rhs' : ''}`,
                `visible-fill-legends-` + visibleFillLegends
            )}
        >
            {!allHidden && (
                <div className="top-legend">
                    <FillsChartToggleLegend
                        className="price"
                        label={priceLabel}
                        fillsGraphContainerLeft={fillsGraphContainerLeft}
                        value={legendData.price}
                        chart={FillsChartType.OrderFills}
                        property="displayPrice"
                        hideLegendProperty="hidePriceLegend"
                        tooltip={priceTooltip}
                    />
                    {displayAvgExecutionLegend && (
                        <FillsChartToggleLegend
                            className="avgExec"
                            fillsGraphContainerLeft={fillsGraphContainerLeft}
                            label={avgExecLabel}
                            value={legendData.avgExec}
                            chart={FillsChartType.OrderFills}
                            property="displayAvgExecution"
                            hideLegendProperty="hideAvgExecutionLegend"
                            tooltip={avgExecutionPriceTooltip}
                        />
                    )}
                    <FillsChartToggleLegend
                        className="vwap"
                        fillsGraphContainerLeft={fillsGraphContainerLeft}
                        label={vwapLegendLabel}
                        value={legendData.vwap}
                        chart={FillsChartType.OrderFills}
                        property="displayVWAP"
                        hideLegendProperty="hideVWAPLegend"
                        tooltip={vwapLegendTooltip}
                        twoColourBorder
                    />
                    {displayPercentageCompletedLegend && (
                        <FillsChartToggleLegend
                            alignDropdown={'right'}
                            className="percentDealt"
                            fillsGraphContainerLeft={fillsGraphContainerLeft}
                            label={percentDeltaLabel}
                            value={legendData.percentDealt}
                            chart={FillsChartType.OrderFills}
                            property="displayPercentageCompleted"
                            hideLegendProperty="hidePercentageCompletedLegend"
                            tooltip={percentCompleteTooltip}
                        />
                    )}
                    {displayIndexLegend && (
                        <FillsChartToggleLegend
                            className="index"
                            fillsGraphContainerLeft={fillsGraphContainerLeft}
                            label={relativePerformanceIndex}
                            value={legendData.index}
                            suffix={legendData.index ? '%' : ''}
                            chart={FillsChartType.OrderFills}
                            property="displayIndex"
                            hideLegendProperty="hideIndexLegend"
                            tooltip={indexTooltip}
                        />
                    )}
                    <OrderFillsHelp side={fillsGraphPortfolioData.side} />
                    {hoveringOverBucket && (
                        <TimeSeriesLegend
                            className="FillsChartLegend bucketTime"
                            containerLeft={fillsGraphContainerLeft}
                            label=""
                            hideDashWhenEmpty={true}
                            value={legendData.bucketTime}
                        />
                    )}
                </div>
            )}
            <OrderFillsChartMultiLegend
                fillsGraphContainerLeft={fillsGraphContainerLeft}
                fillPrice={legendData.fillPrice}
                fillVolume={legendData.fillVolume}
            />
            {showCandleLegends && (
                <OrderFillsChartPriceCandleLegend
                    fillsGraphContainerLeft={fillsGraphContainerLeft}
                    legendData={legendData}
                />
            )}
        </div>
    );
}
